import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/logger'

// API de dibujo compartida entre ChartPage.jsx (privado) y ChartPublicPage.jsx
// (público) — extraída literalmente de ChartPage.jsx (antes EFFECT C, `const
// api = {...}` inline) para que "publicar un indicador una vez" también
// signifique "se dibuja igual en ambos sitios", sin duplicar esta lógica de
// validación. Sin cambios de comportamiento respecto al original.
//
// lightweight-charts no valida price/value/time de forma síncrona: un dato
// corrupto (NaN/null) pasa silencioso por setData/update/createPriceLine y
// sólo lanza "Value is null" de forma DIFERIDA en el siguiente frame de
// render, congelando el gráfico entero hasta recargar la página. Por eso se
// valida aquí, en el único punto de entrada compartido por todos los
// indicadores del registro, y se deja constancia en consola de qué indicador
// y qué valor exacto se ha descartado.
//
// `currentIndicatorIdRef` es un ref mutable (`{ current: string|null }`) que
// el caller debe actualizar antes de invocar `ind.render(...)` de cada
// indicador, para que los mensajes de error identifiquen qué indicador falló.

function setRenderContext(seriesId, phase) {
  if (typeof window !== 'undefined') {
    window.__renderContext = { currentSeriesId: seriesId, phase, at: Date.now() }
  }
}
// Igualdad de contenido entre mapas de coloreado (time → {color, borderColor,
// wickColor}). Permite detectar re-registros no-op (mismo dueño, colores
// idénticos aunque el Map sea otra instancia) y no pedir repintado.
function sameColorMap(a, b) {
  if (a === b) return true
  if (!a || !b) return false
  if (a.size !== b.size) return false
  for (const [t, ca] of a) {
    const cb = b.get(t)
    if (!cb || ca.color !== cb.color || ca.borderColor !== cb.borderColor || ca.wickColor !== cb.wickColor) return false
  }
  return true
}

export function createChartDrawingApi({
  chart,
  candleSeries,
  overlaySeriesRef,
  overlayPriceLinesRef,
  overlayMarkersRef,
  overlayLabelsRawRef,
  overlaySeriesByIndicatorRef,
  overlayPriceLinesByIndicatorRef,
  overlayMarkersByIndicatorRef,
  overlayLabelsByIndicatorRef,
  currentIndicatorIdRef,
  candleColorsRef,
  bumpColorVersion,
  chartControlRef,
  // Token del run de render en curso (lo emiten las páginas, uno por pasada
  // del efecto de indicadores). Se guarda en la entrada del registry para que
  // la página pueda distinguir, al final del run, un dueño que re-registró sus
  // colores (aunque fuera no-op por idénticos) de uno que ya no llama a
  // setCandleColors — solo a este último se le libera el coloreado.
  colorRunId,
}) {
  // Cola de setData aplazados para evitar carreras durante attach/detach de
  // indicadores. Ver `flushPendingSetData`.
  const pendingSetData = []
  let flushScheduled = false

  function flushPendingSetData() {
    flushScheduled = false
    const ctrl = chartControlRef?.current
    if (ctrl && ctrl.isToggling) {
      // Todavía en medio de un toggle; reprogramar el flush para el frame
      // siguiente en que termine.
      scheduleFlush()
      return
    }
    while (pendingSetData.length) {
      const item = pendingSetData.shift()
      const { s, rawSetData, points, id } = item
      setRenderContext(id || id, 'setData')
      try {
        rawSetData(points)
        if (item.applyVisible) item.applyVisible()
      } catch (e) {
        // Estado transitorio del time scale (p.ej. serie de velas aún sin
        // datos tras recrear el chart): reintenta unos frames antes de darse
        // por vencido — un fallo aquí NO debe congelar el render del
        // indicador (antes la excepción subía por el render() y se perdía el
        // resto del dibujo, incluido el coloreado de velas de STQ).
        item.attempts = (item.attempts || 0) + 1
        if (item.attempts < 8) {
          pendingSetData.push(item)
          scheduleFlush()
        } else {
          debugError(`[indicator:${id}] rawSetData falló:`, e, points)
        }
      }
    }
  }

  function scheduleFlush() {
    if (flushScheduled) return
    flushScheduled = true
    requestAnimationFrame(() => {
      // Doble RAF: aseguramos que cualquier RAF de liberación del toggle ya
      // haya corrido (el padre libera isToggling en un RAF al final del render).
      requestAnimationFrame(flushPendingSetData)
    })
  }
  function pushByIndicator(mapRef, value) {
    const id = currentIndicatorIdRef?.current
    if (!id || !mapRef) return
    let list = mapRef.current.get(id)
    if (!list) {
      list = []
      mapRef.current.set(id, list)
    }
    list.push(value)
  }

  function removeByIndicator(mapRef, indicatorId, removeFn) {
    if (!mapRef?.current) return
    const list = mapRef.current.get(indicatorId)
    if (!list) return
    for (const item of list) {
      try { removeFn(item) } catch (e) { debugLog(`[cleanup indicator:${indicatorId}] remove failed:`, e?.message) }
    }
    mapRef.current.delete(indicatorId)
  }

  return {
    addMarker(m) {
      if (m?.time == null || m.time === '' || Number.isNaN(m.time)) { debugError(`[indicator:${currentIndicatorIdRef.current}] addMarker con time inválido, se omite:`, m); return }
      const marked = { ...m, __indicatorId: currentIndicatorIdRef.current }
      overlayMarkersRef.current.push(marked)
      pushByIndicator(overlayMarkersByIndicatorRef, marked)
    },
    addLabel(l) {
      // Un label es válido con `time` (ancla a una vela existente) O con
      // `barsFromLast` (se posiciona N barras después de la última vela,
      // usado por breakout_pattern/liquidity_sweep_pro para las cajas de
      // TP/SL/entry que sobresalen del chart) — updateLabelPositions ya
      // sabe manejar ambos casos.
      if (l?.time == null && l?.barsFromLast == null) { debugError(`[indicator:${currentIndicatorIdRef.current}] addLabel sin time/barsFromLast, se omite:`, l); return }
      const labeled = { ...l, __indicatorId: currentIndicatorIdRef.current }
      overlayLabelsRawRef.current.push(labeled)
      pushByIndicator(overlayLabelsByIndicatorRef, labeled)
    },
    addPriceLine(opts) {
      if (!Number.isFinite(opts?.price)) { debugError(`[indicator:${currentIndicatorIdRef.current}] addPriceLine con price inválido, se omite:`, opts); return }
      const id = opts?.__debugId || `${currentIndicatorIdRef.current}-priceLine-${overlayPriceLinesRef.current.length}`
      setRenderContext(id, 'addPriceLine')
      try {
        const pl = candleSeries.createPriceLine(opts)
        pl.__debugId = id
        pl.__indicatorId = currentIndicatorIdRef.current
        overlayPriceLinesRef.current.push(pl)
        pushByIndicator(overlayPriceLinesByIndicatorRef, pl)
      } catch (e) { debugError(`[indicator:${currentIndicatorIdRef.current}] createPriceLine falló:`, e, opts) }
    },
    addSeries(createFn) {
      const id = `${currentIndicatorIdRef.current}-series-${overlaySeriesRef.current.length}`
      setRenderContext(id, 'addSeries')
      try {
        // Creamos la serie con visible: false y crosshairMarkerVisible: false
        // para evitar que el crosshair de lightweight-charts itere una serie que
        // todavía no ha procesado sus datos. El crosshair pide firstValue() a
        // cada serie visible; si la serie se acaba de crear, firstValue puede
        // ser null y lanzar "Value is null". Se restaura la visibilidad tras
        // setData, cuando firstValue ya está disponible.
        let intendedVisible = true
        const chartWithHiddenSeries = new Proxy(chart, {
          get(target, prop) {
            if (typeof target[prop] === 'function' && /^add.*Series$/.test(prop) && prop !== 'addCustomSeries') {
              return (options) => {
                intendedVisible = options?.visible !== false
                return target[prop]({ ...options, visible: false, crosshairMarkerVisible: false, autoscaleInfoProvider: () => null })
              }
            }
            return target[prop]
          }
        })
        const s = createFn(chartWithHiddenSeries)
        if (!s) return null
        s.__debugId = id
        s.__indicatorId = currentIndicatorIdRef.current
        overlaySeriesRef.current.push(s)
        pushByIndicator(overlaySeriesByIndicatorRef, s)
        const indicatorId = currentIndicatorIdRef.current
        const rawSetData = s.setData.bind(s)
        // La serie ya se creó oculta; la inicializamos vacía y la hacemos
        // visible justo después de setData, cuando firstValue ya existe.
        rawSetData([])
        s.setData = (points) => {
          if (!Array.isArray(points)) { debugError(`[indicator:${indicatorId}] setData esperaba un array:`, points); return }
          const bad = points.find(p => p == null || p.time == null || p.time === '' || Number.isNaN(p.time) || p.value == null || Number.isNaN(p.value) || !Number.isFinite(p.value))
          if (bad) { debugError(`[indicator:${indicatorId}] setData con punto inválido, se omite la serie:`, { bad, points }); return }
          const applyVisible = () => {
            if (intendedVisible) {
              try { s.applyOptions({ visible: true }) } catch (e) { debugError(`[indicator:${indicatorId}] applyOptions visible falló:`, e) }
            }
          }
          if (chartControlRef?.current?.isToggling) {
            pendingSetData.push({ s, rawSetData, points, id: s.__debugId || indicatorId, applyVisible })
            scheduleFlush()
            return
          }
          setRenderContext(s.__debugId || indicatorId, 'setData')
          try {
            const result = rawSetData(points)
            applyVisible()
            return result
          } catch (e) {
            // lightweight-charts puede lanzar "Value is null" si el time
            // scale está en un estado transitorio (p.ej. la serie de velas
            // aún no ha recibido datos tras recrear el chart, o un rango
            // visible que aún no apunta a datos válidos). Antes la excepción
            // subía por el render() del indicador y abortaba el resto del
            // dibujo — en supertrend_quantum eso saltaba el registro del
            // coloreado de velas (setCandleColors va al final del render) y
            // las velas quedaban sin colorear hasta el siguiente render.
            // Aplazamos el setData al siguiente frame y dejamos que el
            // render continúe. Es un estado transitorio auto-recuperable:
            // se registra como warn; el error es solo si los reintentos se
            // agotan (ver flushPendingSetData).
            debugWarn(`[indicator:${indicatorId}] setData falló, se aplaza al siguiente frame:`, e)
            pendingSetData.push({ s, rawSetData, points, id: s.__debugId || indicatorId, applyVisible })
            scheduleFlush()
          }
        }
        const rawUpdate = s.update.bind(s)
        s.update = (p) => {
          setRenderContext(s.__debugId || indicatorId, 'update')
          if (p == null || p.time == null || p.time === '' || Number.isNaN(p.time) || p.value == null || Number.isNaN(p.value) || !Number.isFinite(p.value)) {
            debugError(`[indicator:${indicatorId}] update con punto inválido, se omite:`, p)
            return
          }
          return rawUpdate(p)
        }
        return s
      } catch (e) { debugError(`[indicator:${currentIndicatorIdRef.current}] addSeries failed:`, e); return null }
    },
    addCustomSeries(paneView, options) {
      const id = `${currentIndicatorIdRef.current}-customSeries-${overlaySeriesRef.current.length}`
      setRenderContext(id, 'addCustomSeries')
      try {
        const s = chart.addCustomSeries(paneView, options)
        if (!s) return null
        s.__debugId = id
        s.__indicatorId = currentIndicatorIdRef.current
        overlaySeriesRef.current.push(s)
        pushByIndicator(overlaySeriesByIndicatorRef, s)
        // Inicialización vacía inmediata, misma razón que addSeries: evitar
        // que lightweight-charts lea una serie sin datos en el próximo frame.
        try { s.setData([]) } catch {}
        return s
      } catch (e) { debugError('[indicator] addCustomSeries failed:', e); return null }
    },
    clearIndicator(indicatorId) {
      if (!indicatorId) return
      removeByIndicator(overlaySeriesByIndicatorRef, indicatorId, (s) => chart.removeSeries(s))
      removeByIndicator(overlayPriceLinesByIndicatorRef, indicatorId, (pl) => candleSeries.removePriceLine(pl))
      removeByIndicator(overlayMarkersByIndicatorRef, indicatorId, () => {
        // Los markers se sincronizan globalmente en candleSeries; al eliminar
        // los de un indicador reconstruimos el conjunto completo de markers.
      })
      removeByIndicator(overlayLabelsByIndicatorRef, indicatorId, () => {
        // Los labels HTML se derivan de overlayLabelsRawRef; se reconstruyen
        // tras el render.
      })
      // Filtrar arrays planos y mapas de cualquier residuo
      overlaySeriesRef.current = overlaySeriesRef.current.filter(s => s.__indicatorId !== indicatorId)
      overlayPriceLinesRef.current = overlayPriceLinesRef.current.filter(pl => pl.__indicatorId !== indicatorId)
      overlayMarkersRef.current = overlayMarkersRef.current.filter(m => m.__indicatorId !== indicatorId)
      overlayLabelsRawRef.current = overlayLabelsRawRef.current.filter(l => l.__indicatorId !== indicatorId)
      // Coloreado de velas: si este indicador era el dueño, se libera aquí —
      // restore garantizado al quitar el indicador (bug histórico: las velas
      // quedaban coloreadas por la tendencia tras desactivar supertrend_quantum).
      if (candleColorsRef?.current?.ownerId === indicatorId) {
        candleColorsRef.current = null
        if (bumpColorVersion) bumpColorVersion()
      }
    },
    // Coloreado dinámico de velas (p.ej. supertrend_quantum): un indicador NO
    // debe llamar a candleSeries.setData() por su cuenta — esa serie ya tiene
    // dos escritores (el efecto de datos y el de ticks en vivo de
    // ChartRenderer.jsx) y un tercero independiente es justo la carrera que
    // producía "Value is null" diferido de forma intermitente. Este método
    // solo deja el color en un ref compartido y pide un repintado;
    // ChartRenderer decide cuándo y con qué datos reconstruir la serie.
    //
    // Registry con dueño único: `candleColorsRef.current` es
    // `{ ownerId, colorMap }`. El ownerId es el indicador que registra (el de
    // `currentIndicatorIdRef` en el momento de la llamada) y es quien puede
    // liberarlo vía `clearIndicator(ownerId)` o vía el reset owner-aware de
    // las páginas. Un `colorMap` null con owner vivo significa "dueño activo
    // con coloreado OFF" → ChartRenderer restaura la paleta base.
    setCandleColors(colorMap) {
      if (!candleColorsRef) { debugError(`[indicator:${currentIndicatorIdRef.current}] setCandleColors: candleColorsRef no disponible`); return }
      const ownerId = currentIndicatorIdRef?.current ?? null
      const prev = candleColorsRef.current
      // Un solo dueño a la vez: gana el último que escribe, pero nunca en
      // silencio — si otro indicador tenía colores vivos registrados, se
      // deja constancia del pisado.
      if (prev?.colorMap && prev.ownerId && ownerId && prev.ownerId !== ownerId) {
        debugError(`[indicator:${ownerId}] setCandleColors pisa el coloreado de velas de ${prev.ownerId}`)
      }
      const nextMap = colorMap && colorMap.size > 0 ? colorMap : null
      // No-op por construcción: un re-render del indicador que registra lo
      // MISMO (mismo dueño y colores idénticos) no bumpea colorVersion. Sin
      // este guard, supertrend_quantum — que llama a setCandleColors en CADA
      // render, con un Map nuevo de igual contenido — provocaba un setData
      // completo de la serie principal en cada tick (~3×/s, cazado en staging
      // gracias al log de identidad), y ese churn del color-effect era el
      // actor que movía el eje temporal solo. El token de run SÍ se actualiza
      // (el dueño está vivo y re-registró), pero sin pedir repintado.
      if (prev && prev.ownerId === ownerId && sameColorMap(prev.colorMap, nextMap)) {
        if (colorRunId !== undefined) candleColorsRef.current = { ...prev, runId: colorRunId }
        return
      }
      candleColorsRef.current = { ownerId, colorMap: nextMap, runId: colorRunId }
      if (bumpColorVersion) bumpColorVersion()
    },
  }
}
